import React, { useEffect, useRef, useState } from 'react';
import { Search, User, X as XIcon } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type { PatientListItem } from '../../../services/apiService';

interface PatientPickerProps {
    value: PatientListItem | null;
    onPick: (p: PatientListItem) => void;
    disabled?: boolean;
}

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const PatientPicker: React.FC<PatientPickerProps> = ({ value, onPick, disabled }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState<PatientListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open || query.trim().length < 2) {
            setResults([]);
            return;
        }
        const handle = setTimeout(async () => {
            try {
                setLoading(true);
                const res = await apiService.listPatients({ search: query, limit: 8 });
                setResults(res.data.data || []);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(handle);
    }, [query, open]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    if (value && !open) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md">
                <User className="h-4 w-4 text-brand-text-muted shrink-0" strokeWidth={1.75} aria-hidden />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-brand-text truncate">{value.full_name}</div>
                    <div className="text-xs text-brand-text-muted tabular-nums">{value.patient_number}{value.email && <> · {value.email}</>}</div>
                </div>
                {!disabled && (
                    <button
                        type="button"
                        onClick={() => { setQuery(''); setOpen(true); }}
                        className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer"
                    >
                        Ändern
                    </button>
                )}
            </div>
        );
    }

    return (
        <div ref={wrapRef} className="relative">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none" strokeWidth={1.75} aria-hidden />
                <input
                    type="text"
                    autoFocus
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder="Patient suchen (Name, E-Mail, NIE, Telefon)…"
                    className={`${inputClass} pl-9 pr-9`}
                    disabled={disabled}
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-brand-text-muted hover:text-brand-text cursor-pointer"
                        title="Abbrechen"
                    >
                        <XIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                )}
            </div>

            {open && (
                <div className="absolute z-30 mt-1 w-full rounded-md bg-brand-surface border border-brand-border shadow-lg max-h-72 overflow-y-auto">
                    {query.trim().length < 2 ? (
                        <div className="px-3 py-3 text-xs text-brand-text-muted">Mindestens 2 Zeichen eingeben.</div>
                    ) : loading ? (
                        <div className="px-3 py-3 text-xs text-brand-text-muted">Suche…</div>
                    ) : results.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-brand-text-muted">Keine Treffer.</div>
                    ) : (
                        <ul role="listbox">
                            {results.map((p) => (
                                <li key={p.id}>
                                    <button
                                        type="button"
                                        onClick={() => { onPick(p); setOpen(false); setQuery(''); }}
                                        className="w-full text-left px-3 py-2 hover:bg-brand-surface-2/60 transition-colors cursor-pointer flex items-center gap-3"
                                    >
                                        <User className="h-4 w-4 text-brand-text-muted shrink-0" strokeWidth={1.75} aria-hidden />
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-brand-text truncate">{p.full_name}</div>
                                            <div className="text-xs text-brand-text-muted truncate">
                                                {p.patient_number}{p.email && <> · {p.email}</>}{p.phone_primary && <> · {p.phone_primary}</>}
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default PatientPicker;
